import moment from "moment";
// @ts-ignore - locale import doesn't have types
import "moment/locale/sv";

import { DateStatus } from "../models/Order";
import type { ScheduleWeekItem, ShiftItem } from "../models/Order";
import type { ConsultantAbsence, DateRowItem, ScheduleShiftItem, ShiftItemWithMissingHours } from "../models/Schedule";

export const categorizeDate = (dateString: string): DateStatus => {
	const givenDate = moment(moment(dateString), "YYYY-MM-DD");
	const currentDate = moment();

	if (givenDate.isSame(currentDate, "day")) {
		return DateStatus.WeekDate;
	}
	if (givenDate.isBefore(currentDate, "day")) {
		return DateStatus.DisabledDate;
	} else {
		// Check if the given date is a weekend or weekday
		const dayOfWeek = givenDate.day();
		if (dayOfWeek === 0 || dayOfWeek === 6) {
			return DateStatus.WeekEndDate;
		} else {
			return DateStatus.WeekDate;
		}
	}
};

export const formatSwedishDate = (dateString?: string, format?: "short" | "full"): string => {
	if (!dateString) return "";
	const dayNameThreeChars = moment(moment(dateString), "YYYY-MM-DD")
		.locale("sv")
		.format(format === "short" ? "ddd D MMM" : "dddd D MMMM");
	return dayNameThreeChars.charAt(0).toUpperCase() + dayNameThreeChars.slice(1);
};

export const getDatesForNextWeeks = (numberOfWeeks: number): string[] => {
	const dates = [];
	const currentDate = moment();

	for (let i = 0; i < numberOfWeeks; i++) {
		const dateInString = currentDate.format("YYYY-MM-DD");
		dates.push(dateInString);
		currentDate.add(1, "week");
	}

	return dates;
};

export const getWeekItems = (numberOfWeeks: number, startDate?: string): ScheduleWeekItem[] => {
	const weeks: ScheduleWeekItem[] = [];
	const currentDate = moment(startDate);

	for (let i = 0; i < numberOfWeeks; i++) {
		const weekStart = currentDate.clone().startOf("week");
		const weekNumber = getISOWeekString(currentDate.toDate());
		const dateList: ShiftItem[] = [];

		for (let j = 0; j < 7; j++) {
			const dateItem: ShiftItem = {
				start: weekStart.format("YYYY-MM-DD"),
				break: 0,
				end: weekStart.format("YYYY-MM-DD"),
				flexible: false,
			};
			dateList.push(dateItem);
			weekStart.add(1, "day");
		}

		weeks.push({
			weekNumber,
			dateList,
		});

		currentDate.add(1, "week");
	}
	return weeks;
};

export const getSwedishWeekDayName = (dateString: string): string => {
	const weekDayName = moment(moment(dateString), "YYYY-MM-DD").locale("sv").format("dddd");
	// return weekDayName.charAt(0).toUpperCase() + weekDayName.slice(1);
	return weekDayName;
};

export const getTimeDiffInHours = (startTime: string, endTime: string) => {
	const format = "HH:mm"; // Format for parsing time strings
	const startMoment = moment(startTime, format);
	const endMoment = moment(endTime, format);

	const diffInMilliseconds = endMoment.diff(startMoment); // Get the difference in milliseconds

	return moment.duration(diffInMilliseconds).asHours();
};

const getDateForWeekAndDay = (weekNumber: string, day: string): string => {
	const firstDayOfWeek = moment()
		.year(Number(weekNumber.split("-W")[0]))
		.isoWeek(Number(weekNumber.split("-W")[1]))
		.isoWeekday(1);
	const targetDate = firstDayOfWeek.clone().isoWeekday(day);
	return targetDate.format("YYYY-MM-DD");
};

export const applyCopiedScheduleForSelectedWeek = (
	toBeAppliedWeekNumber: string,
	selectedDateSchedule: ShiftItem,
): ShiftItem => {
	const dateNameOfTheSchedule = getSwedishWeekDayName(selectedDateSchedule.start);
	const dateValue = getDateForWeekAndDay(toBeAppliedWeekNumber, dateNameOfTheSchedule);
	const start = formatDateTime(dateValue, selectedDateSchedule.start);
	const end = formatDateTime(dateValue, selectedDateSchedule.end);
	return {
		start,
		end,
		break: selectedDateSchedule.break,
		isExpanded: selectedDateSchedule.isExpanded,
		tempStatus: "DEFAULT",
	};
};

export const getWeekItemsWithShiftData = (
	shiftItems: ShiftItem[],
	numberOfWeeks: number,
	startDate?: string,
	shouldAddTempState?: boolean,
): ScheduleWeekItem[] => {
	const weeks: ScheduleWeekItem[] = getWeekItems(numberOfWeeks, startDate);
	shiftItems.forEach((shiftItem) => {
		const weekNumber = getISOWeekString(moment(shiftItem.start).toDate());
		const weekItemIndex = weeks.findIndex((wi) => wi.weekNumber === weekNumber);

		if (weekItemIndex >= 0) {
			const dateItemIndex = weeks[weekItemIndex].dateList.findIndex(
				(di) => moment(di.start).format("YYYY-MM-DD") === moment(shiftItem.start).format("YYYY-MM-DD"),
			);
			dateItemIndex >= 0 &&
				weeks[weekItemIndex].dateList.splice(
					dateItemIndex,
					1,
					shouldAddTempState ? { ...shiftItem, tempStatus: "DEFAULT" } : shiftItem,
				);
		}
	});

	return weeks;
};

export const getFormatTime = (date: string): string => {
	return moment(date).format("HH:mm");
};

export const getFormatDate = (date: string | Date): string => {
	return moment(date).format("YYYY-MM-DD");
};

export const getFormatDateWithStartTime = (date: string | Date): string => {
	return moment(date).startOf("day").toISOString();
};

export const getFormatDateWithEndTime = (date: string | Date): string => {
	return moment(date).endOf("day").toISOString();
};

export const getFormatDateAsDateMonth = (date: string): string => {
	return moment(moment(date), "YYYY-MM-DD").locale("sv").format("D MMMM");
};

export const isValidTime = (date: string): boolean => {
	return moment(date).format("HH:mm") !== "00:00";
};

export const formatDateTime = (dateValue: string, timeValue: string): string => {
	const formattedDate = moment(dateValue).format("YYYY-MM-DD");
	const formattedTime = moment(timeValue).format("HH:mm:ss");
	return moment(`${formattedDate}T${formattedTime}`).toISOString();
};

export const getDateList = (startDate: Date, endDate: Date, shiftItems?: ScheduleShiftItem[]): DateRowItem[] => {
	const dateList: DateRowItem[] = [];

	// Create a copy of the start date to avoid modifying the original date object
	let currentDate = new Date(startDate);

	while (currentDate <= endDate) {
		const date = currentDate.getDate();
		const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6; // 0 is Sunday, 6 is Saturday

		dateList.push({
			date: getFormatDate(currentDate),
			isWeekend,
			isToday: moment().isSame(moment(currentDate), "day"),
			shiftItems: shiftItems
				? shiftItems.filter((si) => moment(si.start).isSame(moment(currentDate), "day"))
				: undefined,
		});

		// Move to the next day
		currentDate.setDate(date + 1);
	}
	return dateList;
};

export const isShiftWithinAbsentDate = (shiftDate: ScheduleShiftItem, absentDates?: ConsultantAbsence[]): boolean => {
	const shiftStartDate = moment(shiftDate.start);
	const shiftEndDate = moment(shiftDate.end);

	if (!absentDates) return false;

	for (const absentDate of absentDates) {
		const absentStartDate = moment(absentDate.startDate);
		const absentEndDate = moment(absentDate.endDate);

		if (
			shiftStartDate.isBetween(absentStartDate, absentEndDate, undefined, "[]") ||
			shiftEndDate.isBetween(absentStartDate, absentEndDate, undefined, "[]") ||
			(shiftStartDate.isBefore(absentStartDate) && shiftEndDate.isAfter(absentEndDate))
		) {
			return true;
		}
	}

	return false;
};

export const formatTooltipDateRange = (shiftItem?: ScheduleShiftItem): string => {
	if (!shiftItem) return "";
	const startMoment = moment(shiftItem.start);
	const endMoment = moment(shiftItem.end);

	const formattedDate = startMoment.format("ddd D MMM, HH:mm") + " - " + endMoment.format("HH:mm");
	return formattedDate;
};

export const calculateSlotWidth = (startTime: string, endTime: string, maxWidth: number): number => {
	const rate = maxWidth / (24 * 60);
	const startMinutes = (moment(startTime).hour() * 60 + moment(startTime).minute()) * rate;
	const endMinutes = (moment(endTime).hour() * 60 + moment(endTime).minute()) * rate;
	const slotWidth = endMinutes - startMinutes;
	return slotWidth ?? 0;
};

export const shiftListWithMissingHours = (shifts: ScheduleShiftItem[]): ShiftItemWithMissingHours[] => {
	const dayStart = moment().startOf("day");
	const dayEnd = moment().endOf("day");
	const timeSlots: ShiftItemWithMissingHours[] = [];

	let currentMoment = dayStart.clone();

	for (const shift of shifts) {
		const shiftStart = moment(shift.start);
		const shiftEnd = moment(shift.end);

		if (!shiftStart.isSameOrAfter(shiftEnd)) {
			// Before shift
			if (currentMoment.isBefore(shiftStart)) {
				timeSlots.push({
					start: currentMoment.toISOString(),
					end: shiftStart.toISOString(),
					isValidShift: false,
				});
			}

			// Shift itself
			timeSlots.push({
				start: shiftStart.toISOString(),
				end: shiftEnd.toISOString(),
				isValidShift: true,
				shift: shift,
			});

			// Update current moment to the end of the shift
			currentMoment = shiftEnd.clone().add(1, "second");
		}
	}

	// After the last shift
	if (currentMoment.isBefore(dayEnd)) {
		timeSlots.push({
			start: currentMoment.toISOString(),
			end: dayEnd.toISOString(),
			isValidShift: false,
		});
	}

	return timeSlots;
};

export function getFirstDateOfYear(customDate?: Date): Date {
	const inputDate = customDate || new Date();
	return new Date(inputDate.getFullYear(), 0, 1); // First date of the current year
}

export function getLastDateOfYear(customDate?: Date): Date {
	const inputDate = customDate ? moment(customDate) : moment();
	const lastDayOfYear = inputDate.endOf("year").endOf("day").toDate();

	return lastDayOfYear;
}

export function getFirstDateOfFirstWeekOfYear(customDate: Date): Date {
	const year = moment(customDate).year(); // Get the year from the input date
	const januaryFirst = moment().year(year).month(0).date(1); // January 1st of the input year
	const firstThursday = januaryFirst.clone().startOf("isoWeek"); // Start of ISO week containing January 1st

	return firstThursday.toDate();
}

export function getLastDateOfTwelfthWeekOfYear(customDate: Date): Date {
	const firstDateOfFirstWeek = getFirstDateOfFirstWeekOfYear(customDate);

	// Calculate the first date of the twelfth week (Monday)
	const firstDateOfTwelfthWeek = moment(firstDateOfFirstWeek).add(11, "weeks").startOf("isoWeek");

	// Set time to 23:59:59
	const lastDateOfTwelfthWeek = firstDateOfTwelfthWeek.endOf("isoWeek").toDate();

	return lastDateOfTwelfthWeek;
}

export function getDateOfPreviousTwelfthWeek(customDate: Date): Date {
	const weekDurationInDays = 7; // Number of days in a week

	// Calculate the date of the first day of the week (Monday) for the given date
	const firstDayOfWeek = new Date(customDate);
	firstDayOfWeek.setDate(customDate.getDate() - customDate.getDay() + 1); // Set to Monday of the current week

	// Calculate the date of the first day of the 12th week ago (Monday)
	const firstDayOfPreviousTwelfthWeek = new Date(firstDayOfWeek);
	firstDayOfPreviousTwelfthWeek.setDate(firstDayOfWeek.getDate() - 12 * weekDurationInDays); // 12 weeks ago

	return firstDayOfPreviousTwelfthWeek;
}

export function getLastDateOfNextTwelfthWeek(customDate: Date): Date {
	// Calculate the date of the last day of the week (Sunday) for the given date
	const lastDayOfWeek = moment(customDate).endOf("week").toDate();

	// Calculate the date of the last day of the 12th week from the given date (Sunday)
	const lastDayOfNextTwelfthWeek = moment(lastDayOfWeek).add(12, "weeks").endOf("week").endOf("day").toDate();

	return lastDayOfNextTwelfthWeek;
}

export function getFirstDateOfPreviousYear(customDate: Date): Date {
	const previousYear = customDate.getFullYear() - 1; // Get the previous year
	const firstDayOfPreviousYear = new Date(previousYear, 0, 1); // January 1st of the previous year
	return firstDayOfPreviousYear;
}

export function getLastDateOfPreviousYear(customDate: Date): Date {
	const previousYear = moment(customDate).subtract(1, "year").endOf("year").endOf("day").toDate();
	return previousYear;
}

export function getFirstDateOfNextYear(customDate: Date): Date {
	const nextYear = customDate.getFullYear() + 1; // Get the next year
	const firstDayOfNextYear = new Date(nextYear, 0, 1); // January 1st of the next year
	return firstDayOfNextYear;
}

export function getLastDateOfNextYear(customDate: Date): Date {
	const nextYear = moment(customDate).add(1, "year").endOf("year").endOf("day").toDate();
	return nextYear;
}

export const getShortSwedishMonthName = (monthNumber: number): string => {
	const shortSwedishMonthNames: string[] = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"Maj",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Okt",
		"Nov",
		"Dec",
	];

	if (monthNumber >= 1 && monthNumber <= 12) {
		return shortSwedishMonthNames[monthNumber - 1];
	} else {
		return "";
	}
};

export const getFutureWeekNumber = (initialDate: string | Date, nextWeekCount: number): number => {
	const startDate = moment(initialDate, "YYYY-MM-DD");
	const futureDate = startDate.clone().add(nextWeekCount, "weeks");
	const futureWeekNumber = futureDate.isoWeek();
	return futureWeekNumber;
};

export const addOrRemoveDays = (date: Date, dayCount: number): Date => {
	const newDate = new Date(date);
	newDate.setDate(newDate.getDate() + dayCount);
	return newDate;
};

export const convertToUTC = (inputDateString: string): string => {
	const inputFormat = "YYYY-MM-DDTHH:mm:ssZ";
	const outputFormat = "YYYY-MM-DDTHH:mm:ss.SSS[Z]";

	const convertedDate = moment(inputDateString, inputFormat).utcOffset(0, true).format(outputFormat);

	return convertedDate;
};

export const getYearOfTheDay = (initialDate: string | Date): number => {
	return moment(initialDate).year();
};
interface MonthInfo {
	year: number;
	month: number;
}

export const getMonthNumbersList = (startDate: Date, endDate: Date): MonthInfo[] => {
	const startMoment = moment(startDate);
	const endMoment = moment(endDate);

	const monthNumbersList: MonthInfo[] = [];

	// Iterate through each month and add its year and month number to the list
	while (startMoment.isSameOrBefore(endMoment, "month")) {
		monthNumbersList.push({
			year: startMoment.year(),
			month: startMoment.month() + 1, // month() returns 0-indexed month
		});
		startMoment.add(1, "month");
	}

	return monthNumbersList;
};

interface ISOWeekInfo {
	year: number;
	week: number;
}

export const getISOWeeksList = (startDate: Date, endDate: Date): ISOWeekInfo[] => {
	const startMoment = moment(startDate);
	const endMoment = moment(endDate);

	const isoWeeksList: ISOWeekInfo[] = [];

	// Iterate through each week and add its year and ISO week number to the list
	while (startMoment.isSameOrBefore(endMoment, "week")) {
		isoWeeksList.push({
			year: startMoment.year(),
			week: startMoment.isoWeek(),
		});
		startMoment.add(1, "week");
	}

	return isoWeeksList;
};

export const getISOWeekString = (date: Date): string => {
	const m = moment(date);
	return `${m.isoWeekYear()}-W${m.isoWeek()}`;
};
